import { createReducer, isAnyOf } from "@reduxjs/toolkit";
import { first, last } from "lodash";
import { createResultSetSuccessful, deleteCalculationQueueElementFailure, deleteCalculationQueueElementSuccessful, deleteCalculationQueueRowFailure, deleteCalculationQueueRowSuccessful, deleteResultSetElementSuccessful, deleteResultSetSuccessful, duplicateResultSetElementSuccessful, duplicateResultSetSuccessful, editCalculationQueue, editCalculationQueueElement, editCalculationQueueElementFailure, editCalculationQueueElementSuccessful, editCalculationQueueFailure, editCalculationQueueRow, editCalculationQueueRowFailure, editCalculationQueueRowSuccessful, editCalculationQueueSuccessful, getLineGraphResultSuccessful, getProcessDataViewerResultSuccessful, getSimulationResult, getSimulationResultFailure, getSpaceviewerResultSuccessful, getTableResultSuccessful, moveCalculationElement, moveCalculationElementFailure, moveCalculationElementRow, moveCalculationElementRowFailure, moveCalculationElementRowSuccessful, moveCalculationElementSuccessful, pauseCalculation, pauseCalculationFailure, pauseCalculationSuccessful, setPredictionTime, resetAllResults, selectResultSet, setActiveResultElement, setDuplicateTargetResultSetId, setLineGraphSourceParamsSuccessful, setProcessDataViewerSourceParamsSuccessful, setResultIsFetched, setResultSets, setShouldScrollToRsResultElement, setSpaceviewerAxisSuccessful, setSpaceviewerConfidenceSuccessful, setSpaceviewerDensitySuccessful, setSpaceviewerSourceViewSuccessful, startCalculation, startCalculationFailure, startCalculationSuccessful, stopCalculation, stopCalculationFailure, stopCalculationSuccessful, updateCalculationStatus, updateResultSetElementSuccessful, updateTableElementViewSuccessful, upsertResultElementSuccessful, setShouldInvokePrintResultSet, setProcessDataViewerSourceParams, setLineGraphSourceParams, setCreateEntityFromResultElementDialogData, createEntityFromResultElementSourceSuccessful, setPlotRangeConfigsSuccessful, } from "./actions/results.actions";
import { addInsightSuccessful, addOptimizationSuccessful, addSimulationSuccessful, clearInsightPreview, clearOptimizationPreview, clearPreviewRequest, clearSimulationPreview, editInsightSuccessful, editOptimizationSuccessful, editSimulationSuccessful, getInsightDefaultNameSuccessful, getInsightEssentialCombinationsSuccessful, getInsightOperationDescriptionsSuccessful, getInsightOperationsSuccessful, getInsightScoreSuccessful, getInsightTransformationsSuccessful, getOptimizationDefaultNameSuccessful, getOptimizationPreview, getOptimizationPreviewResultSuccessful, getSimulationDefaultNameSuccessful, initOptimizationPreview, initSimulationPreview, loadWorkspaceSuccessful, persistSimulationPreview, removeInsightOutputScores, removeSimulationRelatedPreviews, resetInsightDefaultName, resetInsightTemporaryScores, resetOptimizationDefaultName, resetOptimizationPreview, resetSimulationPreview, rsWorkspaceAddCellModelSuccessful, rsWorkspaceAddProcessDataSuccessful, rsWorkspaceAddRecipeSuccessful, rsWorkspaceDeleteCellModelSuccessful, rsWorkspaceDeleteInsightSuccessful, rsWorkspaceDeleteOptimizationSuccessful, rsWorkspaceDeleteProcessDataSuccessful, rsWorkspaceDeleteRecipeSuccessful, rsWorkspaceDeleteSimulationSuccessful, saveInsightTemporaryScores, setCurrentInsight, setCurrentOptimization, setCurrentSimulation, setCurrentVariantId, setDisplayMode, setHighlightLeadingNode, setInsightDialogOpen, setInsightExpandedInputSet, setInsightScoreErrored, setInsightValidationErrors, setLockedDialogData, setOptimizationDialogOpen, setOptimizationPreviewAxis, setOptimizationPreviewTimeoutError, setPreviewRequest, setRenameVariantDialogOpen, setResultsRecalculationDialogOpen, setRsWorkspaceHighlightMode, setScenario, setSimulationDialogOpen, setSimulationPreviewData, setSimulationPreviewError, setSimulationPreviewVariables, toggleUnsavedChangesCalculationDialogOpen, updateSimulationScores, } from "./actions/rs-workspace.actions";
import initialState, { simulationPreviewDefaultSelectedVariables, } from "./rs-workspace.store";
import { CancelOutdatedPreviewType, OutputWeight, ResultSetElementType, SimulationRunState, } from "./rs-workspace.types";
import { reset } from "store/knowledge-base-item/knowledge-base-item.actions";
import { InvokeRsSocketMethods } from "api/rs-socket-callbacks";
import { ErrorType } from "api/api-error";
import { GlobalEntityType, RsWorkspaceEntityType } from "shared/types";
import { isNotEmpty } from "utils/validate";
import { checkBasicScoreCalculationConditions, getInsightLatestScoreData, getUpdatedPreviewsDataAfterDeletingSimulationComponent, } from "./utils/insights.utils";
import { prepareLineGraphResultsItem, prepareProcessDataViewerResultsItem, prepareResultSetsStateOnChangeElement, prepareSpaceviewerResultsItem, prepareTabularResultsItem, } from "./utils/results.utils";
import { getEmptySpaceviewerResultSource, isOptimizationPreviewRequestOnlyTemporary, } from "./utils/common.utils";
import { updatePredictionTime } from "./utils/calculations.utils";
export default createReducer(initialState, (builder) => {
    builder
        .addCase(reset, () => initialState)
        .addCase(loadWorkspaceSuccessful, (state, { payload }) => {
        state.state = payload.state === null ? state.state : payload.state;
        state.activeResultSetId = first(payload.state?.resultSets)?.id ?? null;
    })
        .addCase(setPreviewRequest, (state, { payload }) => {
        let untouchedRequests = state.previewRequests.filter(({ type }) => type !== payload.type);
        switch (payload.type) {
            case InvokeRsSocketMethods.CALCULATE_SIMULATION_PREVIEW: {
                const untouchedSimulationRequests = state.previewRequests.filter(({ data, type }) => type === payload.type &&
                    (data.simulationId !== payload.data.simulationId || data.variantId !== payload.data.variantId));
                untouchedRequests = [...untouchedRequests, ...untouchedSimulationRequests];
                break;
            }
            case InvokeRsSocketMethods.CALCULATE_INSIGHT_PREVIEW: {
                const insightRequests = state.previewRequests.filter(({ data, type }) => type === payload.type &&
                    (data.insightId !== payload.data.insightId ||
                        data.outputId !== payload.data.outputId ||
                        data.inputSetId !== payload.data.inputSetId));
                untouchedRequests = [...untouchedRequests, ...insightRequests];
                break;
            }
            case InvokeRsSocketMethods.CALCULATE_OPTIMIZATION_PREVIEW: {
                const requestedElementId = payload.data.elementId;
                const optimizationRequests = state.previewRequests.filter(({ data, type }) => type === payload.type && data.elementId !== requestedElementId);
                untouchedRequests = [...untouchedRequests, ...optimizationRequests];
                if (state.optimizationDialogOpen) {
                    if (state.optimizationPreviews.temporary !== null &&
                        state.optimizationPreviews.temporary.elementId === requestedElementId) {
                        state.optimizationPreviews.temporary.requestId = payload.id;
                    }
                    else {
                        if (state.optimizationPreviews.persisted[requestedElementId]) {
                            state.optimizationPreviews.persisted[requestedElementId].requestId = payload.id;
                            state.optimizationPreviews.persisted[requestedElementId].requestedParameters =
                                payload.data.request.parameters;
                        }
                    }
                }
                else {
                    if (state.optimizationPreviews.persisted[requestedElementId]) {
                        state.optimizationPreviews.persisted[requestedElementId].requestId = payload.id;
                        state.optimizationPreviews.persisted[requestedElementId].requestedParameters =
                            payload.data.request.parameters;
                    }
                }
                break;
            }
        }
        state.previewRequests = [...untouchedRequests, payload];
    })
        .addCase(setDisplayMode, (state, { payload }) => {
        state.mode = payload;
    })
        .addCase(setResultsRecalculationDialogOpen, (state, { payload }) => {
        state.resultsRecalculationDialogOpen = payload;
    })
        .addCase(selectResultSet, (state, { payload }) => {
        state.activeResultSetId = payload;
    })
        .addCase(setSimulationDialogOpen, (state, { payload }) => {
        state.simulationDialogOpen = payload;
    })
        .addCase(setSimulationPreviewData, (state, { payload }) => {
        const currentSocketRequestIdx = state.previewRequests.findIndex(({ id, type }) => id === payload.requestId && type === InvokeRsSocketMethods.CALCULATE_SIMULATION_PREVIEW);
        if (currentSocketRequestIdx === -1) {
            return;
        }
        const currentSocketRequest = state.previewRequests[currentSocketRequestIdx];
        const variantId = currentSocketRequest?.data?.variantId;
        if (state.simulationDialogOpen && state.simulationPreviews.temporary !== null) {
            if (state.simulationPreviews.temporary[`${payload.elementId}_${0}`]) {
                if (payload.elementId === -1 && state.previewRequests[currentSocketRequestIdx].newlyCreatedEntity) {
                    const entityId = state.previewRequests[currentSocketRequestIdx].data.simulationId;
                    const combinedId = `${entityId}_${variantId}`;
                    state.simulationPreviews.persisted[combinedId] = {
                        simulationId: state.simulationPreviews.persisted[combinedId]?.simulationId,
                        selectedVariables: state.simulationPreviews.persisted[combinedId]?.selectedVariables,
                        graphData: payload.graphData,
                    };
                }
                else {
                    const combinedId = `${payload.elementId}_${variantId}`;
                    state.simulationPreviews.temporary[combinedId] = {
                        simulationId: state.simulationPreviews.temporary[combinedId]?.simulationId,
                        selectedVariables: state.simulationPreviews.temporary[combinedId]?.selectedVariables,
                        graphData: payload.graphData,
                    };
                }
            }
            else {
                if (payload.elementId === -1 && state.previewRequests[currentSocketRequestIdx].newlyCreatedEntity) {
                    const entityId = state.previewRequests[currentSocketRequestIdx].data.simulationId;
                    const combinedId = `${entityId}_${variantId}`;
                    if (state.simulationPreviews.temporary[combinedId]) {
                        state.simulationPreviews.temporary[combinedId] = {
                            simulationId: state.simulationPreviews.temporary[combinedId]?.simulationId,
                            selectedVariables: state.simulationPreviews.temporary[combinedId]?.selectedVariables,
                            graphData: payload.graphData,
                        };
                    }
                    else {
                        state.simulationPreviews.persisted[combinedId] = {
                            simulationId: state.simulationPreviews.persisted[combinedId]?.simulationId,
                            selectedVariables: state.simulationPreviews.persisted[combinedId]?.selectedVariables,
                            graphData: payload.graphData,
                        };
                    }
                }
                else {
                    const combinedId = `${payload.elementId}_${variantId}`;
                    state.simulationPreviews.persisted[combinedId] = {
                        simulationId: state.simulationPreviews.persisted[combinedId]?.simulationId,
                        selectedVariables: state.simulationPreviews.persisted[combinedId]?.selectedVariables,
                        graphData: payload.graphData,
                    };
                }
            }
        }
        else {
            if (payload.elementId === -1 && state.previewRequests[currentSocketRequestIdx].newlyCreatedEntity) {
                const entityId = state.previewRequests[currentSocketRequestIdx].data.simulationId;
                const combinedId = `${entityId}_${variantId}`;
                state.simulationPreviews.persisted[combinedId] = {
                    simulationId: state.simulationPreviews.persisted[combinedId]?.simulationId,
                    selectedVariables: state.simulationPreviews.persisted[combinedId]?.selectedVariables,
                    graphData: payload.graphData,
                };
            }
            else {
                const combinedId = `${payload?.elementId}_${variantId}`;
                state.simulationPreviews.persisted[combinedId] = {
                    simulationId: state.simulationPreviews.persisted[combinedId]?.simulationId,
                    selectedVariables: state.simulationPreviews.persisted[combinedId]?.selectedVariables,
                    graphData: payload?.graphData,
                };
            }
        }
        state.previewRequests.splice(currentSocketRequestIdx, 1);
    })
        .addCase(setSimulationPreviewError, (state, { payload: { requestId, data } }) => {
        const currentSocketRequestIdx = state.previewRequests.findIndex(({ id, type }) => id === requestId && type === InvokeRsSocketMethods.CALCULATE_SIMULATION_PREVIEW);
        if (currentSocketRequestIdx === -1) {
            return;
        }
        const request = state.previewRequests[currentSocketRequestIdx];
        const combinedId = `${request.data.simulationId}_${request.data.variantId}`;
        if (state.simulationDialogOpen && state.simulationPreviews.temporary !== null) {
            if (state.simulationPreviews.temporary[combinedId]) {
                state.simulationPreviews.temporary[combinedId] = {
                    ...state.simulationPreviews.temporary[combinedId],
                    socketError: data,
                };
            }
            else {
                state.simulationPreviews.persisted[combinedId] = {
                    ...state.simulationPreviews.persisted[combinedId],
                    socketError: data,
                };
            }
        }
        else {
            state.simulationPreviews.persisted[combinedId] = {
                ...state.simulationPreviews.persisted[combinedId],
                socketError: data,
            };
        }
        state.previewRequests.splice(currentSocketRequestIdx, 1);
    })
        .addCase(initSimulationPreview, (state, { payload }) => {
        const persistedPreviews = {};
        Object.keys(state.simulationPreviews.persisted).forEach((key) => {
            const [simulationId] = key.split("_");
            if (Number(simulationId) === payload) {
                persistedPreviews[key] = {
                    simulationId: payload ?? -1,
                    graphData: state.simulationPreviews.persisted[key]?.graphData || null,
                    selectedVariables: state.simulationPreviews.persisted[key]?.selectedVariables ||
                        simulationPreviewDefaultSelectedVariables,
                    socketError: state.simulationPreviews.persisted[key]?.socketError,
                };
            }
        });
        if (Object.keys(persistedPreviews)?.length) {
            state.simulationPreviews.temporary = persistedPreviews;
            return;
        }
        const newSimulationId = payload ?? -1;
        const defaultVariantId = 0;
        const idOfNewPreview = `${newSimulationId}_${defaultVariantId}`;
        if (state.simulationPreviews.temporary?.[idOfNewPreview])
            return;
        state.simulationPreviews.temporary = {
            [idOfNewPreview]: {
                simulationId: newSimulationId,
                graphData: null,
                selectedVariables: simulationPreviewDefaultSelectedVariables,
            },
        };
    })
        .addCase(getSimulationDefaultNameSuccessful, (state, { payload }) => {
        state.simulationDefaultName = payload;
    })
        .addCase(addSimulationSuccessful, (state, { payload }) => {
        if (payload.simulationId !== null) {
            const previewRequests = state.previewRequests.filter(({ type, data }) => data.simulationId === -1 && type === InvokeRsSocketMethods.CALCULATE_SIMULATION_PREVIEW);
            previewRequests.forEach((previewRequest) => {
                const previewRequestIndex = state.previewRequests?.indexOf(previewRequest);
                if (previewRequestIndex !== -1) {
                    state.previewRequests[previewRequestIndex].newlyCreatedEntity = true;
                    state.previewRequests[previewRequestIndex].data.simulationId = payload.simulationId;
                }
            });
            if (!state.simulationPreviews.temporary)
                return;
            if (Object.keys(state.simulationPreviews.temporary)?.length > 0) {
                Object.keys(state.simulationPreviews.temporary).forEach((key) => {
                    const [, variantId] = key.split("_");
                    if (!state.simulationPreviews.temporary?.[key])
                        return;
                    state.simulationPreviews.temporary[`${payload.simulationId}_${variantId}`] = {
                        ...state.simulationPreviews.temporary[key],
                        simulationId: payload.simulationId,
                    };
                    delete state.simulationPreviews.temporary[key];
                });
            }
        }
        state.state = payload.state;
        state.simulationDialogOpen = false;
        state.currentSimulationId = null;
        state.currentVariantId = 0;
    })
        .addCase(editSimulationSuccessful, (state, { payload }) => {
        state.state = payload.state;
        state.simulationDialogOpen = false;
        state.currentSimulationId = null;
        state.currentVariantId = 0;
    })
        .addCase(rsWorkspaceDeleteSimulationSuccessful, (state, { payload }) => {
        state.state = payload;
        state.simulationDialogOpen = false;
        state.currentSimulationId = null;
    })
        .addCase(persistSimulationPreview, (state, { payload }) => {
        if (payload === undefined || !state.simulationPreviews.temporary)
            return;
        Object.keys(state.simulationPreviews.temporary).forEach((key) => {
            const [simulationId] = key.split("_");
            if (Number(simulationId) === payload) {
                if (!state.simulationPreviews.temporary?.[key])
                    return;
                state.simulationPreviews.persisted[key] = {
                    ...state.simulationPreviews.temporary[key],
                    simulationId: payload,
                };
            }
        });
        state.simulationPreviews.temporary = null;
    })
        .addCase(resetSimulationPreview, (state, { payload }) => {
        if (!state.simulationPreviews.temporary) {
            return;
        }
        if (Object.keys(state.simulationPreviews.temporary).some((key) => {
            const [simulationId] = key.split("_");
            return Number(simulationId) === payload;
        })) {
            state.simulationPreviews.temporary = null;
        }
    })
        .addCase(setCurrentSimulation, (state, { payload }) => {
        state.currentSimulationId = payload;
    })
        .addCase(setSimulationPreviewVariables, (state, { payload }) => {
        if (!state.simulationPreviews.temporary)
            return;
        Object.keys(state.simulationPreviews.temporary).forEach((key) => {
            const [simulationId] = key.split("_");
            if (Number(simulationId) === payload.simulationId) {
                if (!state.simulationPreviews.temporary?.[key])
                    return;
                state.simulationPreviews.temporary[key].selectedVariables = payload.selectedVariables;
            }
        });
    })
        .addCase(setCurrentVariantId, (state, { payload }) => {
        state.currentVariantId = payload;
    })
        .addCase(setRenameVariantDialogOpen, (state, { payload }) => {
        state.renameVariantDialogOpen = payload;
    })
        .addCase(addInsightSuccessful, (state, { payload }) => {
        if (payload.insightId !== null) {
            const untouchedInsightsData = state.persistedInsightsData.filter((item) => item.insightId !== -1);
            const temporaryInsightData = state.persistedInsightsData.find((item) => item.insightId === -1 && !item.isOriginal);
            if (temporaryInsightData) {
                state.persistedInsightsData = [
                    ...untouchedInsightsData,
                    {
                        ...temporaryInsightData,
                        insightId: payload.insightId,
                    },
                    {
                        ...temporaryInsightData,
                        insightId: payload.insightId,
                        isOriginal: true,
                    },
                ];
            }
        }
        state.state = payload.state;
        state.insightDialogOpen = false;
        state.currentInsightId = null;
    })
        .addCase(editInsightSuccessful, (state, { payload }) => {
        const insight = payload.state.insights.find(({ id }) => payload.id === id);
        if (insight) {
            insight.state.outputs.forEach((output) => {
                if (!checkBasicScoreCalculationConditions(output, payload.processVariables, state.state.recipes)) {
                    state.persistedInsightsData = state.persistedInsightsData.map((insightEntry) => {
                        if (insightEntry.insightId !== payload.id) {
                            return insightEntry;
                        }
                        const filteredOutputScores = insightEntry.scores.filter((scoreItem) => scoreItem.outputId !== output.id);
                        return { ...insightEntry, scores: filteredOutputScores };
                    });
                }
            });
        }
        state.state = payload.state;
        state.insightDialogOpen = false;
        state.currentInsightId = null;
    })
        .addCase(rsWorkspaceDeleteInsightSuccessful, (state, { payload }) => {
        state.state = payload;
        state.insightDialogOpen = false;
        state.currentInsightId = null;
    })
        .addCase(resetInsightDefaultName, (state) => {
        state.insightDefaultName = null;
    })
        .addCase(getInsightDefaultNameSuccessful, (state, { payload }) => {
        state.insightDefaultName = payload;
    })
        .addCase(getInsightOperationsSuccessful, (state, { payload }) => {
        state.insightOperations = payload;
    })
        .addCase(getInsightOperationDescriptionsSuccessful, (state, { payload }) => {
        state.insightOperationDescriptions = payload;
    })
        .addCase(getInsightTransformationsSuccessful, (state, { payload }) => {
        state.insightTransformations = payload;
    })
        .addCase(getInsightEssentialCombinationsSuccessful, (state, { payload }) => {
        state.insightEssentialCombinations = payload;
    })
        .addCase(setInsightDialogOpen, (state, { payload }) => {
        state.insightDialogOpen = payload;
    })
        .addCase(setCurrentInsight, (state, { payload }) => {
        state.currentInsightId = payload;
    })
        .addCase(getInsightScoreSuccessful, (state, { payload }) => {
        const currentSocketRequestIdx = state.previewRequests.findIndex(({ id }) => id === payload.requestId);
        if (currentSocketRequestIdx !== -1) {
            const currentPreviewRequestData = state.previewRequests.find(({ id, type }) => id === payload.requestId && type === InvokeRsSocketMethods.CALCULATE_INSIGHT_PREVIEW);
            const outputIdOfReceivedScore = currentPreviewRequestData?.data.outputId;
            const inputSetIdOfReceivedScore = currentPreviewRequestData?.data.inputSetId;
            const baseNewInsightData = {
                insightId: payload.insightId,
                scores: [
                    {
                        outputId: outputIdOfReceivedScore,
                        inputSetId: inputSetIdOfReceivedScore,
                        score: payload.result,
                        weight: payload.weight,
                    },
                ],
                expandedInputSetIndex: 0,
                isOriginal: false,
            };
            const existingInsightData = getInsightLatestScoreData(state.persistedInsightsData, payload.insightId);
            const originalInsightData = state.persistedInsightsData.find((item) => item.insightId === payload.insightId && item.isOriginal);
            if (existingInsightData) {
                const insightDataItemsToKeep = state.persistedInsightsData.filter((item) => item.insightId !== payload.insightId);
                const outputDataToKeep = existingInsightData.scores.filter((item) => item.outputId !== outputIdOfReceivedScore || item.inputSetId !== inputSetIdOfReceivedScore);
                const updatedOutputsScores = [
                    ...outputDataToKeep,
                    {
                        outputId: outputIdOfReceivedScore,
                        inputSetId: inputSetIdOfReceivedScore,
                        score: payload.result,
                        weight: payload.weight,
                    },
                ];
                const untouchedOriginalScores = originalInsightData?.scores.filter(({ inputSetId, outputId }) => inputSetId !== inputSetIdOfReceivedScore || outputId !== outputIdOfReceivedScore) ?? [];
                const alreadyAddedToOriginal = originalInsightData?.scores.some(({ inputSetId, outputId }) => inputSetId === inputSetIdOfReceivedScore && outputId === outputIdOfReceivedScore);
                const originalScores = alreadyAddedToOriginal
                    ? originalInsightData?.scores
                    : [
                        ...untouchedOriginalScores,
                        {
                            outputId: outputIdOfReceivedScore,
                            inputSetId: inputSetIdOfReceivedScore,
                            score: payload.result,
                            weight: payload.weight,
                        },
                    ];
                state.persistedInsightsData = [
                    ...insightDataItemsToKeep,
                    { ...existingInsightData, scores: updatedOutputsScores },
                    ...(originalInsightData ? [{ ...originalInsightData, scores: originalScores ?? [] }] : []),
                ];
            }
            else {
                state.persistedInsightsData = [
                    ...state.persistedInsightsData,
                    baseNewInsightData,
                    ...(!originalInsightData ? [{ ...baseNewInsightData, isOriginal: true }] : []),
                ];
            }
            state.previewRequests.splice(currentSocketRequestIdx, 1);
        }
    })
        .addCase(setInsightScoreErrored, (state, { payload: { requestId, errors } }) => {
        const currentSocketRequestIdx = state.previewRequests.findIndex(({ id, type }) => id === requestId && type === InvokeRsSocketMethods.CALCULATE_INSIGHT_PREVIEW);
        if (currentSocketRequestIdx === -1) {
            return;
        }
        const currentPreviewRequestData = state.previewRequests[currentSocketRequestIdx];
        const outputIdOfReceivedScore = currentPreviewRequestData?.data.outputId;
        const inputSetIdOfReceivedScore = currentPreviewRequestData?.data.inputSetId;
        const insightIdOfReceivedScore = currentPreviewRequestData?.data.insightId === null ? -1 : currentPreviewRequestData?.data.insightId;
        const existingInsightData = getInsightLatestScoreData(state.persistedInsightsData, insightIdOfReceivedScore);
        if (existingInsightData) {
            const insightDataItemsToKeep = state.persistedInsightsData.filter((item) => item.insightId !== insightIdOfReceivedScore ||
                (item.insightId === insightIdOfReceivedScore && item.isOriginal));
            const outputDataToKeep = existingInsightData.scores.filter((item) => item.outputId !== outputIdOfReceivedScore || item.inputSetId !== inputSetIdOfReceivedScore);
            const updatedOutputsScores = [
                ...outputDataToKeep,
                {
                    outputId: outputIdOfReceivedScore,
                    inputSetId: inputSetIdOfReceivedScore,
                    weight: OutputWeight.Normal,
                    score: { data: null, error: errors, isSuccess: false },
                },
            ];
            state.persistedInsightsData = [
                ...insightDataItemsToKeep,
                { ...existingInsightData, scores: updatedOutputsScores },
            ];
        }
        else {
            const newErroredInsightScore = {
                insightId: insightIdOfReceivedScore,
                scores: [
                    {
                        outputId: outputIdOfReceivedScore,
                        inputSetId: inputSetIdOfReceivedScore,
                        weight: OutputWeight.Normal,
                        score: { data: null, isSuccess: false, error: errors },
                    },
                ],
                expandedInputSetIndex: 0,
                isOriginal: false,
            };
            state.persistedInsightsData = [
                ...state.persistedInsightsData,
                newErroredInsightScore,
                { ...newErroredInsightScore, isOriginal: true },
            ];
        }
        state.previewRequests.splice(currentSocketRequestIdx, 1);
    })
        .addCase(removeInsightOutputScores, (state, { payload }) => {
        const insightDataItemsToKeep = state.persistedInsightsData.filter((item) => item.insightId !== payload.insightId);
        const insightOriginalData = state.persistedInsightsData.find((item) => item.insightId === payload.insightId && item.isOriginal);
        const insightScoreData = getInsightLatestScoreData(state.persistedInsightsData, payload.insightId);
        const untouchedScores = insightScoreData?.scores.filter((item) => item.outputId !== payload.outputId) ?? [];
        if (!!payload.inputSetIds && payload.inputSetIds?.length > 0) {
            const removedOutputScores = insightScoreData?.scores.filter((item) => item.outputId === payload.outputId) ?? [];
            const outputScoresForUntouchedInputSets = removedOutputScores?.filter((item) => !payload.inputSetIds?.includes(item.inputSetId));
            untouchedScores.push(...outputScoresForUntouchedInputSets);
        }
        state.persistedInsightsData = [
            ...insightDataItemsToKeep,
            ...(insightOriginalData ? [insightOriginalData] : []),
            ...(insightScoreData ? [{ ...insightScoreData, scores: untouchedScores }] : []),
        ];
    })
        .addCase(resetInsightTemporaryScores, (state, { payload }) => {
        const insightDataItemsToKeep = state.persistedInsightsData.filter((item) => item.insightId !== payload);
        const insightOriginalData = state.persistedInsightsData.find((item) => item.insightId === payload && item.isOriginal);
        const isNewInsight = payload === -1;
        const updatedInsightsData = [
            {
                ...insightOriginalData,
                scores: insightOriginalData?.scores ?? [],
                insightId: !isNotEmpty(insightOriginalData?.insightId)
                    ? -1
                    : (insightOriginalData?.insightId ?? -1),
                expandedInputSetIndex: insightOriginalData?.expandedInputSetIndex ?? 0,
            },
            ...(insightOriginalData ? [{ ...insightOriginalData, isOriginal: false }] : []),
        ];
        state.persistedInsightsData = [...insightDataItemsToKeep, ...(isNewInsight ? [] : updatedInsightsData)];
    })
        .addCase(saveInsightTemporaryScores, (state, { payload }) => {
        const insightDataItemsToKeep = state.persistedInsightsData.filter((item) => item.insightId !== payload);
        const insightLatestData = getInsightLatestScoreData(state.persistedInsightsData, payload);
        state.persistedInsightsData = [
            ...insightDataItemsToKeep,
            ...(insightLatestData ? [insightLatestData, { ...insightLatestData, isOriginal: true }] : []),
        ];
    })
        .addCase(updateSimulationScores, (state, { payload }) => {
        state.persistedInsightsData = payload;
    })
        .addCase(setInsightExpandedInputSet, (state, { payload }) => {
        const existingInsightData = getInsightLatestScoreData(state.persistedInsightsData, payload.insightId);
        if (existingInsightData) {
            const insightDataItemsToKeep = state.persistedInsightsData.filter((item) => item.insightId !== (payload.insightId ?? -1) ||
                (item.insightId === (payload.insightId ?? -1) && item.isOriginal));
            state.persistedInsightsData = [
                ...insightDataItemsToKeep,
                { ...existingInsightData, expandedInputSetIndex: payload.inputSetIndex },
            ];
        }
        else {
            state.persistedInsightsData = [
                ...state.persistedInsightsData,
                {
                    insightId: payload.insightId ?? -1,
                    scores: [],
                    expandedInputSetIndex: payload.inputSetIndex,
                    isOriginal: false,
                },
                {
                    insightId: payload.insightId ?? -1,
                    scores: [],
                    expandedInputSetIndex: payload.inputSetIndex,
                    isOriginal: true,
                },
            ];
        }
    })
        .addCase(removeSimulationRelatedPreviews, (state, { payload }) => {
        const { updatedScoresData, updatedPersistedOptimizationPreviews } = getUpdatedPreviewsDataAfterDeletingSimulationComponent(state.state.simulations, state.state.insights, state.state.optimizations, state.optimizationPreviews, state.persistedInsightsData, payload.entityType, payload.id);
        state.persistedInsightsData = updatedScoresData;
        state.optimizationPreviews.persisted = updatedPersistedOptimizationPreviews;
    })
        .addCase(setInsightValidationErrors, (state, { payload }) => {
        state.insightErrors = payload;
    })
        .addCase(addOptimizationSuccessful, (state, { payload }) => {
        state.state = payload.state;
        state.optimizationDialogOpen = false;
        state.currentOptimizationId = null;
        if (payload.optimizationId !== null) {
            const previewItem = {
                ...(state.optimizationPreviews.temporary ?? {}),
                elementId: payload.optimizationId,
            };
            state.optimizationPreviews.persisted[payload.optimizationId] = previewItem;
            state.optimizationPreviews.temporary = null;
        }
    })
        .addCase(editOptimizationSuccessful, (state, { payload }) => {
        state.state = payload.state;
        state.optimizationDialogOpen = false;
        state.currentOptimizationId = null;
        if (payload.optimizationId !== null &&
            (state.optimizationPreviews.temporary?.elementId === payload.optimizationId ||
                state.optimizationPreviews.temporary === null)) {
            state.optimizationPreviews.persisted[payload.optimizationId] = {
                ...(state.optimizationPreviews.temporary ?? {}),
                elementId: payload.optimizationId,
            };
            state.optimizationPreviews.temporary = null;
            delete state.optimizationPreviews.persisted[payload.optimizationId];
        }
    })
        .addCase(rsWorkspaceDeleteOptimizationSuccessful, (state, { payload }) => {
        state.state = payload;
        state.optimizationDialogOpen = false;
        state.currentOptimizationId = null;
    })
        .addCase(resetOptimizationPreview, (state, { payload }) => {
        if (state.optimizationPreviews.temporary?.elementId === payload) {
            state.optimizationPreviews.temporary = { elementId: payload, requestedParameters: [] };
        }
    })
        .addCase(resetOptimizationDefaultName, (state) => {
        state.optimizationDefaultName = null;
    })
        .addCase(getOptimizationDefaultNameSuccessful, (state, { payload }) => {
        state.optimizationDefaultName = payload;
    })
        .addCase(setOptimizationDialogOpen, (state, { payload }) => {
        state.optimizationDialogOpen = payload;
    })
        .addCase(setCurrentOptimization, (state, { payload }) => {
        state.currentOptimizationId = payload;
    })
        .addCase(initOptimizationPreview, (state, { payload }) => {
        const persistedItem = state.optimizationPreviews.persisted[payload ?? -1];
        if (!isNotEmpty(persistedItem) && payload !== -1 && payload !== null) {
            state.optimizationPreviews.persisted[payload] = { elementId: payload, requestedParameters: [] };
        }
        state.optimizationPreviews.temporary = {
            ...(persistedItem || {}),
            elementId: payload ?? -1,
        };
    })
        .addCase(getOptimizationPreview, (state, { payload }) => {
        const elementId = payload.elementId ?? -1;
        const { parameters: requestedParameters } = payload.request || {};
        if (state.optimizationDialogOpen) {
            if (state.optimizationPreviews.temporary !== null &&
                state.optimizationPreviews.temporary.elementId === payload.elementId) {
                state.optimizationPreviews.temporary = {
                    requestedParameters,
                    elementId,
                };
                return;
            }
        }
        state.optimizationPreviews.persisted[elementId] = {
            requestedParameters,
        };
    })
        .addCase(getOptimizationPreviewResultSuccessful, (state, { payload }) => {
        const { requestId, result, elementId } = payload;
        const currentSocketRequestIdx = state.previewRequests.findIndex(({ id, type }) => id === requestId && type === InvokeRsSocketMethods.CALCULATE_OPTIMIZATION_PREVIEW);
        if (currentSocketRequestIdx === -1) {
            return;
        }
        if (state.optimizationPreviews.temporary?.requestId === requestId) {
            state.optimizationPreviews.temporary = {
                ...state.optimizationPreviews.temporary,
                result,
            };
        }
        for (const optimizationId in state.optimizationPreviews.persisted) {
            if (state.optimizationPreviews.persisted[optimizationId].requestId === requestId &&
                `${optimizationId}` === `${elementId}`) {
                state.optimizationPreviews.persisted[optimizationId] = {
                    ...state.optimizationPreviews.persisted[optimizationId],
                    result,
                };
            }
        }
        state.previewRequests.splice(currentSocketRequestIdx, 1);
    })
        .addCase(setOptimizationPreviewAxis, (state, { payload }) => {
        if (state.optimizationPreviews.temporary === null) {
            return;
        }
        state.optimizationPreviews.temporary.selectedAxisIndexes = payload;
    })
        .addCase(clearOptimizationPreview, (state, { payload }) => {
        const ids = state.state.optimizations
            .filter((item) => {
            return item.id === payload;
        })
            .map(({ id }) => id);
        ids.forEach((id) => {
            delete state.optimizationPreviews.persisted[id];
            if (state.optimizationPreviews.temporary?.elementId === id) {
                state.optimizationPreviews.temporary = null;
            }
        });
    })
        .addCase(setOptimizationPreviewTimeoutError, (state, { payload }) => {
        if (!payload || payload?.errors?.[0].errorType !== ErrorType.Timeout) {
            return;
        }
        if (!!state.optimizationPreviews.temporary &&
            state.optimizationPreviews.temporary?.requestId === payload.requestId) {
            state.optimizationPreviews.temporary.result = {
                isSuccess: false,
                data: undefined,
                errorMessage: payload?.errors?.[0].text,
            };
        }
        const previewRequest = state.previewRequests?.find(({ id, type }) => type === InvokeRsSocketMethods.CALCULATE_OPTIMIZATION_PREVIEW && id === payload.requestId);
        const persistedOptimizationPreview = state.optimizationPreviews.persisted[previewRequest?.data.elementId];
        if (persistedOptimizationPreview) {
            state.optimizationPreviews.persisted[previewRequest?.data.elementId].result = {
                isSuccess: false,
                data: undefined,
                errorMessage: payload?.errors?.[0].text,
            };
        }
    })
        .addCase(clearPreviewRequest, (state, { payload }) => {
        switch (payload.previewType) {
            case CancelOutdatedPreviewType.Simulation:
                state.previewRequests = [...state.previewRequests].filter(({ data, type }) => type !== InvokeRsSocketMethods.CALCULATE_SIMULATION_PREVIEW ||
                    (type === InvokeRsSocketMethods.CALCULATE_SIMULATION_PREVIEW &&
                        data.simulationId !== payload.elementId));
                break;
            case CancelOutdatedPreviewType.Insight:
                state.previewRequests = [...state.previewRequests].filter(({ data, type }) => type !== InvokeRsSocketMethods.CALCULATE_INSIGHT_PREVIEW ||
                    (type === InvokeRsSocketMethods.CALCULATE_INSIGHT_PREVIEW &&
                        data.insightId !== payload.elementId));
                break;
            case CancelOutdatedPreviewType.Optimization:
                if (isOptimizationPreviewRequestOnlyTemporary(state.optimizationPreviews)) {
                    state.previewRequests = [...state.previewRequests].filter(({ data, type }) => type !== InvokeRsSocketMethods.CALCULATE_OPTIMIZATION_PREVIEW ||
                        (type === InvokeRsSocketMethods.CALCULATE_OPTIMIZATION_PREVIEW &&
                            data.elementId !== payload.elementId));
                }
                break;
        }
    })
        .addCase(setResultSets, (state, { payload }) => {
        state.state.resultSets = payload;
    })
        .addCase(setLockedDialogData, (state, { payload }) => {
        state.lockedDialogData = payload;
    })
        .addCase(setActiveResultElement, (state, { payload }) => {
        state.activeResultElement = payload;
    })
        .addCase(setDuplicateTargetResultSetId, (state, { payload }) => {
        state.duplicateTargetResultSetId = payload;
    })
        .addCase(setShouldScrollToRsResultElement, (state, { payload }) => {
        state.shouldScrollToResultElement = payload;
    })
        .addCase(duplicateResultSetElementSuccessful, (state) => {
        state.activeResultElement = null;
        state.activeResultSetId = state.duplicateTargetResultSetId;
        state.duplicateTargetResultSetId = null;
        state.shouldScrollToResultElement = true;
    })
        .addCase(duplicateResultSetSuccessful, (state, { payload }) => {
        const duplicatedResultSet = last(payload.resultSets);
        state.activeResultSetId = duplicatedResultSet?.id ?? state.activeResultSetId;
    })
        .addCase(setShouldInvokePrintResultSet, (state, { payload }) => {
        state.shouldInvokePrintResultSet = payload;
    })
        .addCase(updateTableElementViewSuccessful, (state, { payload }) => {
        state.state.resultSets = prepareResultSetsStateOnChangeElement(state, payload);
    })
        .addCase(upsertResultElementSuccessful, (state, { payload }) => {
        state.state.resultSets = prepareResultSetsStateOnChangeElement(state, payload);
    })
        .addCase(setResultIsFetched, (state) => {
        state.resultIsFetching = false;
    })
        .addCase(getSimulationResult, (state, { payload: { resultSetId, elementId, reset } }) => {
        state.resultIsFetching = true;
        const element = state.state.resultSets
            .find(({ id }) => resultSetId === id)
            ?.elements.find(({ id }) => elementId === id);
        const baseData = { resultSetId, resultSetElementId: elementId };
        switch (element?.type) {
            case ResultSetElementType.Table:
                state.calculationTabularResults = prepareTabularResultsItem(state.calculationTabularResults, {
                    ...baseData,
                    elementType: ResultSetElementType.Table,
                    rows: element?.rows?.map(({ id }) => ({
                        rowId: id,
                        fetching: !reset,
                        columns: [],
                        accuracy: null,
                        state: SimulationRunState.UNKNOWN,
                    })),
                });
                break;
            case ResultSetElementType.SpaceViewer:
                state.calculationSpaceviewerResults = prepareSpaceviewerResultsItem(state.calculationSpaceviewerResults, {
                    ...baseData,
                    sources: (element?.sources || []).map(({ id }) => getEmptySpaceviewerResultSource(id)),
                });
                break;
            case ResultSetElementType.ProcessDataViewer:
                state.calculationProcessDataViewerResults = prepareProcessDataViewerResultsItem(state.calculationProcessDataViewerResults, {
                    ...baseData,
                    results: (element?.sources ?? []).map((source) => ({
                        sourceId: source.id ?? 0,
                        state: SimulationRunState.UNKNOWN,
                        fetching: !reset,
                        elementType: ResultSetElementType.ProcessDataViewer,
                        accuracy: null,
                        hasErrors: false,
                        errors: [],
                        points: [],
                    })),
                }, element);
                break;
            case ResultSetElementType.LineGraph:
                state.calculationLineGraphResults = prepareLineGraphResultsItem(state.calculationLineGraphResults, {
                    ...baseData,
                    results: (element?.sources ?? []).map((source) => ({
                        sourceId: source.id ?? 0,
                        state: SimulationRunState.UNKNOWN,
                        fetching: !reset,
                        elementType: ResultSetElementType.LineGraph,
                        accuracy: null,
                        errors: [],
                        hasErrors: false,
                    })),
                });
                break;
        }
    })
        .addCase(getTableResultSuccessful, (state, { payload }) => {
        state.calculationTabularResults = prepareTabularResultsItem(state.calculationTabularResults, payload);
    })
        .addCase(getSpaceviewerResultSuccessful, (state, { payload }) => {
        state.calculationSpaceviewerResults = prepareSpaceviewerResultsItem(state.calculationSpaceviewerResults, payload);
    })
        .addCase(getProcessDataViewerResultSuccessful, (state, { payload }) => {
        const element = state.state.resultSets
            .find(({ id }) => payload.resultSetId === id)
            ?.elements.find(({ id }) => payload.resultSetElementId === id);
        state.calculationProcessDataViewerResults = prepareProcessDataViewerResultsItem(state.calculationProcessDataViewerResults, payload, (element || {}), false, state.state.simulations || [], state.state.optimizations || [], state.state.insights || [], state.state.recipes || [], state.persistedInsightsData.filter((item) => !item.isOriginal), payload?.processVariables || [], state.state.processData || [], payload?.parameterTypes);
    })
        .addCase(getLineGraphResultSuccessful, (state, { payload }) => {
        state.calculationLineGraphResults = prepareLineGraphResultsItem(state.calculationLineGraphResults, payload);
    })
        .addCase(getSimulationResultFailure, (state, { payload }) => {
        const { type, resultSetId, elementId } = payload;
        const error = "Calculation data is unavailable";
        switch (type) {
            case ResultSetElementType.SpaceViewer:
                state.calculationSpaceviewerResults = prepareSpaceviewerResultsItem(state.calculationSpaceviewerResults, {
                    resultSetId,
                    resultSetElementId: elementId,
                    sources: (state.state.resultSets
                        .find(({ id }) => id === resultSetId)
                        ?.elements.find(({ id }) => id === elementId)).sources.map(({ id }) => getEmptySpaceviewerResultSource(id)),
                }, error);
                break;
            case ResultSetElementType.LineGraph:
                state.calculationLineGraphResults = prepareLineGraphResultsItem(state.calculationLineGraphResults, {
                    resultSetId,
                    resultSetElementId: elementId,
                    results: [],
                }, error);
                break;
            case ResultSetElementType.ProcessDataViewer:
                state.calculationProcessDataViewerResults = prepareProcessDataViewerResultsItem(state.calculationProcessDataViewerResults, {
                    resultSetId,
                    resultSetElementId: elementId,
                    results: [],
                }, {}, !!error);
                break;
            case ResultSetElementType.Table:
                {
                    const element = state.state.resultSets
                        .find(({ id }) => id === resultSetId)
                        ?.elements.find(({ id }) => id === elementId);
                    state.calculationTabularResults = prepareTabularResultsItem(state.calculationTabularResults, {
                        resultSetId,
                        resultSetElementId: elementId,
                        elementType: ResultSetElementType.Table,
                        rows: [],
                    }, element, error);
                }
                break;
        }
    })
        .addCase(resetAllResults, (state) => {
        //TODO reset another results
        state.calculationTabularResults = [];
        state.calculationSpaceviewerResults = [];
        state.calculationProcessDataViewerResults = [];
    })
        .addCase(clearSimulationPreview, (state, { payload }) => {
        const { entityType } = payload;
        const simulationIds = state.state.simulations
            .filter(({ recipeId, cellModelId, id }) => {
            switch (entityType) {
                case GlobalEntityType.Recipe:
                    return recipeId === payload.id;
                case GlobalEntityType.CellModel:
                    return cellModelId === payload.id;
                case RsWorkspaceEntityType.Simulation:
                default:
                    return id === payload.id;
            }
        })
            .map(({ id }) => id);
        simulationIds.forEach((id) => {
            Object.keys(state.simulationPreviews.persisted).forEach((key) => {
                const [simulationId] = key.split("_");
                if (Number(simulationId) === id) {
                    delete state.simulationPreviews.persisted[key];
                }
            });
            if (!state.simulationPreviews.temporary)
                return;
            if (Object.keys(state.simulationPreviews.temporary).some((key) => {
                const [simulationId] = key.split("_");
                return Number(simulationId) === id;
            })) {
                state.simulationPreviews.temporary = null;
            }
        });
    })
        .addCase(clearInsightPreview, (state, { payload }) => {
        state.persistedInsightsData = state.persistedInsightsData.filter(({ insightId }) => insightId !== payload);
    })
        .addCase(setRsWorkspaceHighlightMode, (state, { payload }) => {
        state.isHighlightModeActive = payload;
    })
        .addCase(setHighlightLeadingNode, (state, { payload }) => {
        state.highlightLeadingNode = payload;
    })
        .addCase(deleteResultSetElementSuccessful, (state, { payload }) => {
        const { resultSetId, resultSetElementId } = payload;
        const filterFn = (item) => resultSetId !== item.resultSetId || resultSetElementId !== item.resultSetElementId;
        state.calculationTabularResults = state.calculationTabularResults.filter(filterFn);
        state.calculationSpaceviewerResults = state.calculationSpaceviewerResults.filter(filterFn);
        state.calculationLineGraphResults = state.calculationLineGraphResults.filter(filterFn);
        state.calculationProcessDataViewerResults = state.calculationProcessDataViewerResults.filter(filterFn);
    })
        .addCase(deleteResultSetSuccessful, (state, { payload }) => {
        const filterFn = ({ resultSetId }) => resultSetId !== payload;
        state.calculationTabularResults = state.calculationTabularResults.filter(filterFn);
        state.calculationSpaceviewerResults = state.calculationSpaceviewerResults.filter(filterFn);
        state.calculationLineGraphResults = state.calculationLineGraphResults.filter(filterFn);
        state.calculationProcessDataViewerResults = state.calculationProcessDataViewerResults.filter(filterFn);
    })
        .addCase(setPredictionTime, (state, { payload }) => {
        state.state.calculation = updatePredictionTime(state.state.calculation, payload);
        state.state.calculation.predictionTimeInProgress = false;
    })
        .addCase(setCreateEntityFromResultElementDialogData, (state, { payload }) => {
        state.createEntityFromResultElementDialogData = payload;
    })
        .addCase(createEntityFromResultElementSourceSuccessful, (state, { payload }) => {
        state.state = payload === null ? state.state : payload;
        state.createEntityFromResultElementDialogData = {
            elementId: null,
            sourceId: null,
        };
    })
        .addCase(toggleUnsavedChangesCalculationDialogOpen, (state) => {
        state.unsavedChangesCalculationDialogOpen = !state.unsavedChangesCalculationDialogOpen;
    })
        .addMatcher(isAnyOf(setLineGraphSourceParams, setProcessDataViewerSourceParams), (state, { payload }) => {
        const element = (state.state.resultSets
            .find(({ id }) => payload.resultSetId === id)
            ?.elements.find(({ id }) => id === payload.elementId) || {});
        state.state.resultSets = prepareResultSetsStateOnChangeElement(state, {
            ...element,
            changing: true,
            resultSetId: payload.resultSetId,
        });
    })
        .addMatcher(isAnyOf(createResultSetSuccessful, deleteResultSetSuccessful), (state) => ({
        ...state,
        activeResultSetId: last(state.state.resultSets)?.id ?? null,
    }))
        .addMatcher(isAnyOf(startCalculationSuccessful, updateCalculationStatus), (state, { payload }) => {
        const payloadWithCurrentPredictionTime = {
            ...payload.calculation,
            elements: payload.calculation.elements.map((element) => {
                const existingElement = state.state.calculation.elements.find(({ resultElementId, resultSetId }) => element.resultSetId === resultSetId && element.resultElementId === resultElementId);
                return {
                    ...element,
                    rows: element.rows.map((row) => {
                        const existingRow = existingElement?.rows?.find(({ id }) => row.id === id);
                        return {
                            ...row,
                            predictionTime: existingRow?.predictionTime,
                        };
                    }),
                };
            }),
        };
        state.state.calculation = payloadWithCurrentPredictionTime;
        state.state.calculationStatus = payload.calculationStatus;
    })
        .addMatcher(isAnyOf(startCalculation, stopCalculation, pauseCalculation), (state) => {
        state.state.calculationStatusIsChanging = true;
    })
        .addMatcher(isAnyOf(startCalculationSuccessful, stopCalculationSuccessful, pauseCalculationSuccessful, startCalculationFailure, stopCalculationFailure, pauseCalculationFailure), (state) => {
        state.state.calculationStatusIsChanging = false;
    })
        .addMatcher(isAnyOf(setSpaceviewerAxisSuccessful, setSpaceviewerSourceViewSuccessful, setSpaceviewerConfidenceSuccessful, setSpaceviewerDensitySuccessful, updateResultSetElementSuccessful, setProcessDataViewerSourceParamsSuccessful, setLineGraphSourceParamsSuccessful, setPlotRangeConfigsSuccessful), (state, { payload }) => {
        state.state.resultSets = prepareResultSetsStateOnChangeElement(state, {
            ...payload,
            changing: false,
        });
    })
        .addMatcher(isAnyOf(pauseCalculationSuccessful, stopCalculationSuccessful, editCalculationQueueSuccessful, editCalculationQueueRowSuccessful, editCalculationQueueElementSuccessful, deleteCalculationQueueRowSuccessful, deleteCalculationQueueElementSuccessful, moveCalculationElementSuccessful, moveCalculationElementRowSuccessful), (state, { payload }) => {
        state.state.calculation = payload.calculation;
        state.state.calculationStatus = payload.calculationStatus;
    })
        .addMatcher(isAnyOf(editCalculationQueue, editCalculationQueueRow, editCalculationQueueElement, moveCalculationElement, moveCalculationElementRow), (state) => {
        state.state.calculation.saving = true;
        state.state.calculation.predictionTimeInProgress = true;
    })
        .addMatcher(isAnyOf(editCalculationQueueFailure, editCalculationQueueRowFailure, editCalculationQueueElementFailure, deleteCalculationQueueRowFailure, deleteCalculationQueueElementFailure, moveCalculationElementFailure, moveCalculationElementRowFailure, editCalculationQueueSuccessful, editCalculationQueueRowSuccessful, editCalculationQueueElementSuccessful, deleteCalculationQueueRowSuccessful, deleteCalculationQueueElementSuccessful, moveCalculationElementSuccessful, moveCalculationElementRowSuccessful), (state) => {
        state.state.calculation.saving = false;
    })
        .addMatcher(isAnyOf(rsWorkspaceAddCellModelSuccessful, rsWorkspaceAddRecipeSuccessful, rsWorkspaceAddProcessDataSuccessful, rsWorkspaceDeleteProcessDataSuccessful, rsWorkspaceDeleteCellModelSuccessful, rsWorkspaceDeleteRecipeSuccessful, setScenario), (state, { payload }) => ({
        ...state,
        state: payload,
    }));
});
